"use client";

import type { SyringeSize } from "@/lib/calculator/types";

interface SyringeSvgProps {
	syringeSize: SyringeSize;
	fillMl: number;
	/**
	 * Insulin-syringe calibration: U-100 (default) or U-40. Drives the IU readout +
	 * the "X IU / Y IU" labels so the syringe agrees with the cockpit's calibration-aware
	 * big number. U-100 → 1 ml = 100 IU; U-40 → 1 ml = 40 IU. Defaults to 100 so every
	 * existing caller (and the U-100 path) is byte-identical.
	 */
	calibration?: 40 | 100;
	className?: string;
	compact?: boolean;
	/**
	 * Tailwind text-color class that drives the fill + target strike via `currentColor`
	 * (e.g. "text-primary", "text-success", "text-warning"). The per-peptide accent.
	 * Overfill always falls back to the destructive token.
	 */
	accentClassName?: string;
}

// Token-driven colors: we set `color` on the <svg> so fills/strokes can use currentColor,
// and use CSS custom properties (var(--token)) for the structural metal/line tones. This
// keeps everything on semantic tokens — no raw hex, no nested oklch().
const COLOR_METAL = "var(--muted-foreground)";
const COLOR_LINE = "var(--border)";
const COLOR_LINE_STRONG = "var(--muted-foreground)";

export function SyringeSvg({
	syringeSize,
	fillMl,
	calibration = 100,
	className,
	compact = false,
	accentClassName = "text-primary",
}: SyringeSvgProps) {
	const fillPercent = Math.min(fillMl / syringeSize, 1);
	const isOverfill = fillMl > syringeSize;

	// IU readout follows the chosen calibration (U-100 default → ml × 100; U-40 → ml × 40),
	// so the displayed IU + the barrel scale match the cockpit's calibration-aware big number.
	const fillIU = Math.round(fillMl * calibration);
	// Scale follows the SELECTED syringe AND calibration (1.0 ml = 100 IU at U-100, 40 IU at U-40).
	const maxIU = Math.round(syringeSize * calibration);

	// Accent vs. overfill: overfill forces destructive, otherwise the peptide accent
	// flows in through `currentColor` (the accentClassName sets `color`).
	const fillColor = isOverfill ? "var(--destructive)" : "currentColor";

	if (compact) {
		// Compact inline syringe with tick marks + fill indicator (recipe / sticky).
		const compactWidth = 280;
		const compactHeight = 44;
		const barrelLeft = 20;
		const barrelRight = compactWidth - 30;
		const barrelWidth = barrelRight - barrelLeft;
		const barrelY = 8;
		const barrelH = 18;
		const compactFillWidth = fillPercent * barrelWidth;

		const compactTicks = Array.from({ length: 11 }, (_, i) => i / 10);

		return (
			<div className={`${accentClassName} ${className ?? ""}`}>
				<svg
					viewBox={`0 0 ${compactWidth} ${compactHeight}`}
					width={compactWidth}
					height={compactHeight}
					className="block h-auto w-full"
					aria-label={`Syringe showing ${fillIU} IU of ${maxIU} IU`}
					role="img"
				>
					{/* Needle */}
					<polygon
						points={`2,${barrelY + barrelH / 2} ${barrelLeft},${barrelY + 4} ${barrelLeft},${barrelY + barrelH - 4}`}
						fill={COLOR_METAL}
						opacity={0.6}
					/>

					{/* Barrel background */}
					<rect
						x={barrelLeft}
						y={barrelY}
						width={barrelWidth}
						height={barrelH}
						rx={3}
						fill={isOverfill ? "var(--destructive)" : "currentColor"}
						fillOpacity={0.1}
						stroke={COLOR_LINE}
						strokeWidth={1}
					/>

					{/* Fill */}
					{compactFillWidth > 0 && (
						<rect
							x={barrelLeft}
							y={barrelY + 1.5}
							width={Math.min(compactFillWidth, barrelWidth)}
							height={barrelH - 3}
							rx={2}
							fill={fillColor}
							opacity={0.78}
						/>
					)}

					{/* Tick marks */}
					{compactTicks.map((t, i) => {
						const tx = barrelLeft + t * barrelWidth;
						const isMajor = i % 5 === 0;
						const tickH = isMajor ? barrelH : barrelH * 0.4;
						const tickTop = barrelY + (barrelH - tickH) / 2;
						return (
							<line
								key={i}
								x1={tx}
								y1={tickTop}
								x2={tx}
								y2={tickTop + tickH}
								stroke={isMajor ? COLOR_LINE_STRONG : COLOR_LINE}
								strokeWidth={isMajor ? 1.2 : 0.8}
								strokeOpacity={isMajor ? 0.7 : 1}
							/>
						);
					})}

					{/* Target strike — "draw up to here" */}
					{compactFillWidth > 0 && !isOverfill && (
						<>
							<line
								x1={barrelLeft + compactFillWidth}
								y1={barrelY - 2}
								x2={barrelLeft + compactFillWidth}
								y2={barrelY + barrelH + 2}
								stroke="currentColor"
								strokeWidth={2}
								strokeLinecap="round"
							/>
							<circle cx={barrelLeft + compactFillWidth} cy={barrelY - 3} r={2.4} fill="currentColor" />
						</>
					)}

					{/* Plunger */}
					<rect
						x={barrelRight}
						y={barrelY - 2}
						width={3}
						height={barrelH + 4}
						rx={1.5}
						fill={COLOR_LINE_STRONG}
					/>
					<rect
						x={barrelRight + 3}
						y={barrelY + barrelH * 0.2}
						width={12}
						height={barrelH * 0.6}
						rx={1.5}
						fill={COLOR_METAL}
						opacity={0.6}
					/>

					{/* Label text */}
					<text
						x={compactWidth / 2}
						y={compactHeight - 2}
						textAnchor="middle"
						fontSize={10}
						fill={isOverfill ? "var(--destructive)" : COLOR_LINE_STRONG}
						fontFamily="inherit"
					>
						{`${fillIU} IU / ${maxIU} IU (${syringeSize} ml)`}
					</text>
				</svg>
			</div>
		);
	}

	// Full SVG syringe (desktop).
	const svgWidth = 320;
	const svgHeight = 80;
	const needleX = 16;
	const needleY = 36;
	const needleLen = 24;
	const barrelX = needleX + needleLen;
	const barrelY = 20;
	const barrelWidth = 220;
	const barrelHeight = 32;
	const plungerX = barrelX + barrelWidth;
	const plungerHandleWidth = 20;

	const fillWidth = fillPercent * barrelWidth;

	const ticks = Array.from({ length: 11 }, (_, i) => i / 10);

	return (
		<div className={`${accentClassName} ${className ?? ""}`}>
			<svg
				viewBox={`0 0 ${svgWidth} ${svgHeight}`}
				width={svgWidth}
				height={svgHeight}
				className="block h-auto w-full"
				aria-label={`Syringe showing ${fillIU} IU of ${maxIU} IU`}
				role="img"
			>
				{/* Needle */}
				<polygon
					points={`${needleX},${needleY} ${barrelX},${barrelY + barrelHeight * 0.25} ${barrelX},${barrelY + barrelHeight * 0.75}`}
					fill={COLOR_METAL}
					opacity={0.6}
				/>

				{/* Barrel background */}
				<rect
					x={barrelX}
					y={barrelY}
					width={barrelWidth}
					height={barrelHeight}
					rx={4}
					fill={isOverfill ? "var(--destructive)" : "currentColor"}
					fillOpacity={0.12}
					stroke={COLOR_LINE}
					strokeWidth={1.5}
				/>

				{/* Fill rect */}
				{fillWidth > 0 && (
					<rect
						x={barrelX}
						y={barrelY + 2}
						width={Math.min(fillWidth, barrelWidth)}
						height={barrelHeight - 4}
						rx={3}
						fill={fillColor}
						opacity={0.8}
					/>
				)}

				{/* Tick marks */}
				{ticks.map((t, i) => {
					const tx = barrelX + t * barrelWidth;
					const isMajor = i === 0 || i === 5 || i === 10;
					const tickHeight = isMajor ? barrelHeight : barrelHeight * 0.5;
					const tickY = barrelY + (barrelHeight - tickHeight) / 2;
					return (
						<line
							key={i}
							x1={tx}
							y1={tickY}
							x2={tx}
							y2={tickY + tickHeight}
							stroke={isMajor ? COLOR_LINE_STRONG : COLOR_LINE}
							strokeWidth={isMajor ? 1.5 : 1}
							strokeOpacity={isMajor ? 0.7 : 1}
						/>
					);
				})}

				{/* Target strike marker — vertical accent line + dot at fillIU/maxIU.
				    Marks exactly how far to draw the plunger for this dose. */}
				{fillWidth > 0 && !isOverfill && (
					<>
						<line
							x1={barrelX + fillWidth}
							y1={barrelY - 5}
							x2={barrelX + fillWidth}
							y2={barrelY + barrelHeight + 5}
							stroke="currentColor"
							strokeWidth={2.5}
							strokeLinecap="round"
						/>
						<circle cx={barrelX + fillWidth} cy={barrelY - 6} r={3.2} fill="currentColor" />
					</>
				)}

				{/* Plunger line */}
				<rect
					x={plungerX}
					y={barrelY - 4}
					width={4}
					height={barrelHeight + 8}
					rx={2}
					fill={COLOR_LINE_STRONG}
				/>
				{/* Plunger handle */}
				<rect
					x={plungerX + 4}
					y={barrelY + barrelHeight * 0.2}
					width={plungerHandleWidth}
					height={barrelHeight * 0.6}
					rx={2}
					fill={COLOR_METAL}
					opacity={0.6}
				/>

				{/* Label text */}
				<text
					x={svgWidth / 2}
					y={svgHeight - 4}
					textAnchor="middle"
					fontSize={11}
					fill={isOverfill ? "var(--destructive)" : COLOR_LINE_STRONG}
					fontFamily="inherit"
				>
					{`${fillIU} IU / ${maxIU} IU (${syringeSize} ml)`}
				</text>
			</svg>
		</div>
	);
}
